/**
 * KizunaAI latency guide: endpoint detection tuning.
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Tune Sentence Breaks and Latency',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'KizunaAI translates a sentence once it decides you have finished it. If translations arrive too late, or sentences are cut in the middle, <strong>Endpoint Detection Tuning</strong> changes how that decision is made. The defaults suit most conversations; change one setting at a time and try it in a short session. This page is about the Soniox engine: with the Qwen engine, the server ends a turn when the speaker changes or after a pause, and the one setting is <strong>Silence Duration</strong> under VAD Settings, from 0.5 to 6 seconds (2.5 s by default), shorter for faster translations, longer to keep slow sentences whole.',
  },
  steps: [
    {
      title: 'Open the tuning settings',
      content:
        'Open <strong>Settings</strong>, switch to <strong>Advanced</strong> with the button at the top right of the panel, and choose the <strong>Provider</strong> tab. Scroll to <strong>Endpoint Detection Tuning</strong>. Changes apply from the next session.',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: 'Endpoint Sensitivity',
      content:
        'How readily a pause counts as the end of a sentence, from −1 to 1 (default 0). Higher values end sentences sooner, so translations arrive faster but sentences may be cut off early. Lower values wait for stronger evidence that you have finished.',
    },
    {
      title: 'Max Pause Before Finalizing',
      content:
        'A hard limit, from 500 to 3000 ms (default 2000 ms): once you stop speaking, the sentence is closed after at most this long. Raise it if you often pause mid-sentence and your sentences get split; lower it for faster translations.',
    },
    {
      title: 'Latency Reduction Level',
      content:
        'From <strong>0 — Default</strong> through <strong>Lower latency</strong> and <strong>Even lower latency</strong> to <strong>Most aggressive</strong>. Each step closes sentences more eagerly to cut delay.',
    },
  ],
  tips: {
    title: 'Tips',
    items: [
      'Translations arrive late: try raising Endpoint Sensitivity a little, or a higher Latency Reduction Level.',
      'Sentences are cut in the middle: lower Endpoint Sensitivity, or raise Max Pause Before Finalizing.',
      'Pausing briefly at the end of each sentence helps whatever the settings.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'How do I go back to the defaults?',
        answer: 'Set Endpoint Sensitivity to 0.0, Max Pause Before Finalizing to 2000 ms and Latency Reduction Level to 0 — Default.',
      },
      {
        question: 'Is there a push-to-talk mode?',
        answer: 'Yes. Under Speech Mode in Settings, choose Push-to-Talk: audio is sent only while you hold Space or the microphone button, and releasing ends the sentence. Push-to-Translate is the same, except that while you are not holding, your own voice goes straight into the virtual microphone. The settings on this page only apply in Auto mode.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '调整断句与延迟',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content:
      'KizunaAI 会在判断你说完一句话后翻译这句话。如果翻译来得太晚，或者句子在中间被切断，可以通过<strong>断句检测调节</strong>改变这一判断方式。默认值适合大多数对话；每次只改一项设置，并在一次简短的会话中试用。本页介绍的是 Soniox 引擎：使用 Qwen 引擎时，由服务器在说话人切换或停顿后结束一轮，唯一的设置是 VAD 设置下的<strong>静音时长</strong>，范围 0.5 到 6 秒（默认 2.5 秒）：调短翻译更快，调长则能让说得慢的句子保持完整。',
  },
  steps: [
    {
      title: '打开调节设置',
      content:
        '打开<strong>设置</strong>，用面板右上角的按钮切换到<strong>高级</strong>，然后选择<strong>提供商</strong>标签页。滚动到<strong>断句检测调节</strong>。更改从下一次会话开始生效。',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: '断句灵敏度',
      content:
        '决定停顿有多容易被当作一句话的结尾，范围为 −1 到 1（默认 0）。数值越高，句子结束得越早，翻译来得更快，但句子可能会被过早切断。数值越低，会等到有更明确的迹象表明你已经说完。',
    },
    {
      title: '断句前最大停顿时长',
      content:
        '一个硬性上限，范围为 500 到 3000 ms（默认 2000 ms）：你停止说话后，句子最多在这段时间后结束。如果你经常在句中停顿、导致句子被拆开，请调高它；想让翻译更快，就调低它。',
    },
    {
      title: '延迟降低级别',
      content:
        '从 <strong>0 — 默认</strong>，经过<strong>更低延迟</strong>和<strong>进一步降低延迟</strong>，到<strong>最积极</strong>。每提高一级，结束句子就更积极，以缩短延迟。',
    },
  ],
  tips: {
    title: '提示',
    items: [
      '翻译来得太晚：试着把断句灵敏度稍微调高，或选择更高的延迟降低级别。',
      '句子在中间被切断：调低断句灵敏度，或调高断句前最大停顿时长。',
      '无论如何设置，在每句话结尾稍作停顿都会有帮助。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '如何恢复默认值？',
        answer: '将断句灵敏度设为 0.0，断句前最大停顿时长设为 2000 ms，延迟降低级别设为 0 — 默认。',
      },
      {
        question: '有按键说话模式吗？',
        answer: '有。在设置的“语音模式”下选择“按住说话”：按住空格键或麦克风按钮时才发送语音，松开就结束这一句。“按住翻译”与之相同，只是松开时你的原声会直接进入虚拟麦克风。本页的设置只在“自动”模式下起作用。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content:
      '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: '文の区切りと遅延を調整する',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content:
      'KizunaAI は、あなたが文を言い終えたと判断した時点でその文を翻訳します。翻訳が届くのが遅すぎる場合や、文が途中で切れてしまう場合は、<strong>発話終了検出の調整</strong>でその判断のしかたを変えられます。既定値はたいていの会話に合っています。一度に 1 つの設定だけを変え、短いセッションで試してください。このページは Soniox エンジンについてのものです。Qwen エンジンでは、話者が変わったときか、少し間が空いたときに、サーバー側でターンを終わらせます。設定は VAD設定の<strong>無音時間</strong>だけで、0.5～6 秒（既定は 2.5 秒）の範囲で指定します。短くすると翻訳が速く届き、長くすると、ゆっくり話す文を途中で区切らずにまとめられます。',
  },
  steps: [
    {
      title: '調整項目を開く',
      content:
        '<strong>設定</strong>を開き、パネル右上のボタンで<strong>詳細</strong>に切り替えて、<strong>プロバイダー</strong>タブを選びます。<strong>発話終了検出の調整</strong>までスクロールします。変更は次のセッションから適用されます。',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: '発話終了検出の感度',
      content:
        '間をどれだけ文の終わりとみなしやすいかを、−1 から 1 の範囲で指定します（既定値 0）。値を大きくすると文が早く区切られ、翻訳は速く届きますが、文が途中で切れることがあります。値を小さくすると、言い終えたことがよりはっきりするまで待ちます。',
    },
    {
      title: '確定までの最大ポーズ時間',
      content:
        '500～3000 ms の範囲で指定する上限です（既定値 2000 ms）。話すのをやめてから、長くてもこの時間が経つと文が確定します。文の途中でよく間を置き、文が分かれてしまう場合は大きく、翻訳を速くしたい場合は小さくします。',
    },
    {
      title: '遅延削減レベル',
      content:
        '<strong>0 — 標準</strong>から<strong>遅延を低減</strong>、<strong>さらに遅延を低減</strong>を経て<strong>最も積極的</strong>まであります。段階が上がるほど文を早めに区切り、遅延を減らします。',
    },
  ],
  tips: {
    title: 'ヒント',
    items: [
      '翻訳が遅れて届く場合：発話終了検出の感度を少し上げるか、遅延削減レベルを上げてみてください。',
      '文が途中で切れる場合：発話終了検出の感度を下げるか、確定までの最大ポーズ時間を長くしてください。',
      'どの設定でも、文の終わりに少し間を置くと効果があります。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: '既定値に戻すには？',
        answer: '発話終了検出の感度を 0.0、確定までの最大ポーズ時間を 2000 ms、遅延削減レベルを 0 — 標準に設定します。',
      },
      {
        question: 'プッシュトゥトークのモードはありますか？',
        answer: 'はい。設定の「音声モード」で「プッシュトゥトーク」を選びます。スペースキーまたはマイクボタンを押している間だけ音声が送られ、離すとその文が終わります。「プッシュトゥトランスレート」も同じですが、押していない間はあなたの声がそのまま仮想マイクに入ります。このページの設定は「自動」モードでのみ有効です。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content:
      'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: '문장 끊기와 지연 조정',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content:
      'KizunaAI는 내가 문장을 끝냈다고 판단하면 그 문장을 번역합니다. 번역이 너무 늦게 오거나 문장이 중간에 잘린다면 <strong>발화 종료 감지 조정</strong>으로 그 판단 방식을 바꿀 수 있습니다. 기본값은 대부분의 대화에 알맞습니다. 한 번에 하나의 설정만 바꾸고 짧은 세션으로 시험해 보세요. 이 페이지는 Soniox 엔진에 관한 내용입니다. Qwen 엔진에서는 화자가 바뀌거나 말이 잠시 멈추면 서버가 한 턴을 끝내며, 설정은 VAD 설정의 <strong>침묵 지속 시간</strong> 하나뿐입니다. 0.5초에서 6초까지(기본값 2.5초)이며, 짧을수록 번역이 빨라지고 길수록 느리게 말하는 문장도 끊기지 않고 온전히 유지됩니다.',
  },
  steps: [
    {
      title: '조정 설정 열기',
      content:
        '<strong>설정</strong>을 열고 패널 오른쪽 위의 버튼으로 <strong>고급</strong>으로 전환한 다음 <strong>제공자</strong> 탭을 선택하세요. <strong>발화 종료 감지 조정</strong>까지 스크롤하세요. 변경 사항은 다음 세션부터 적용됩니다.',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: '발화 종료 감도',
      content:
        '멈춤을 얼마나 쉽게 문장의 끝으로 볼지를 −1에서 1 사이로 정합니다(기본값 0). 값이 높을수록 문장이 빨리 끝나 번역이 더 빨리 오지만, 문장이 일찍 잘릴 수 있습니다. 값이 낮을수록 말을 끝냈다는 더 확실한 근거를 기다립니다.',
    },
    {
      title: '확정 전 최대 일시정지 시간',
      content:
        '500~3000 ms 범위의 절대 상한입니다(기본값 2000 ms). 말을 멈추면 늦어도 이 시간이 지난 뒤 문장이 확정됩니다. 문장 중간에 자주 멈춰서 문장이 나뉜다면 값을 높이고, 번역을 더 빨리 받으려면 낮추세요.',
    },
    {
      title: '지연 감소 수준',
      content:
        '<strong>0 — 기본값</strong>에서 <strong>지연 시간 감소</strong>와 <strong>지연 시간 더 감소</strong>를 거쳐 <strong>가장 적극적</strong>까지 있습니다. 단계가 올라갈수록 문장을 더 적극적으로 끝내 지연을 줄입니다.',
    },
  ],
  tips: {
    title: '팁',
    items: [
      '번역이 늦게 온다면: 발화 종료 감도를 조금 높이거나 지연 감소 수준을 더 높여 보세요.',
      '문장이 중간에 잘린다면: 발화 종료 감도를 낮추거나 확정 전 최대 일시정지 시간을 늘리세요.',
      '설정과 관계없이 각 문장 끝에서 잠깐 멈추면 도움이 됩니다.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '기본값으로 되돌리려면 어떻게 하나요?',
        answer: '발화 종료 감도를 0.0, 확정 전 최대 일시정지 시간을 2000 ms, 지연 감소 수준을 0 — 기본값으로 설정하세요.',
      },
      {
        question: '눌러서 말하기(push-to-talk) 모드가 있나요?',
        answer: '있습니다. 설정의 “음성 모드”에서 “푸시 투 토크”를 선택하세요. 스페이스 키나 마이크 버튼을 누르고 있는 동안에만 음성이 전송되고, 손을 떼면 문장이 끝납니다. “푸시 투 트랜슬레이트”도 같지만, 누르고 있지 않은 동안에는 내 목소리가 그대로 가상 마이크로 들어갑니다. 이 페이지의 설정은 “자동” 모드에서만 적용됩니다.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content:
      '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Ajusta las pausas entre frases y la latencia',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content:
      'KizunaAI traduce una frase en cuanto decide que la has terminado. Si las traducciones llegan demasiado tarde, o las frases se cortan a la mitad, <strong>Ajuste de detección de fin de intervención</strong> cambia cómo se toma esa decisión. Los valores predeterminados sirven para la mayoría de las conversaciones; cambia un ajuste cada vez y pruébalo en una sesión corta. Esta página trata del motor de Soniox: con el motor de Qwen, el servidor termina un turno cuando cambia el hablante o tras una pausa, y el único ajuste es <strong>Duración del silencio</strong> en Configuración VAD, de 0,5 a 6 segundos (2,5 s de forma predeterminada), más corta para traducciones más rápidas, más larga para mantener enteras las frases lentas.',
  },
  steps: [
    {
      title: 'Abre los ajustes',
      content:
        'Abre <strong>Configuración</strong>, cambia a <strong>Avanzado</strong> con el botón de la parte superior derecha del panel y elige la pestaña <strong>Proveedor</strong>. Desplázate hasta <strong>Ajuste de detección de fin de intervención</strong>. Los cambios se aplican a partir de la siguiente sesión.',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: 'Sensibilidad de fin de intervención',
      content:
        'Con qué facilidad una pausa cuenta como el final de una frase, de −1 a 1 (predeterminado: 0). Los valores altos terminan las frases antes, así que las traducciones llegan más rápido, pero las frases pueden cortarse demasiado pronto. Los valores bajos esperan una señal más clara de que has terminado.',
    },
    {
      title: 'Pausa máxima antes de finalizar',
      content:
        'Un límite fijo, de 500 a 3000 ms (predeterminado: 2000 ms): cuando dejas de hablar, la frase se cierra como mucho tras ese tiempo. Súbelo si sueles hacer pausas a mitad de frase y tus frases se dividen; bájalo para obtener traducciones más rápidas.',
    },
    {
      title: 'Nivel de reducción de latencia',
      content:
        'Desde <strong>0 — Predeterminado</strong>, pasando por <strong>Menor latencia</strong> y <strong>Latencia aún menor</strong>, hasta <strong>Más agresivo</strong>. Cada nivel cierra las frases antes para reducir el retraso.',
    },
  ],
  tips: {
    title: 'Consejos',
    items: [
      'Las traducciones llegan tarde: prueba a subir un poco la Sensibilidad de fin de intervención o a elegir un Nivel de reducción de latencia más alto.',
      'Las frases se cortan a la mitad: baja la Sensibilidad de fin de intervención o sube la Pausa máxima antes de finalizar.',
      'Hacer una breve pausa al final de cada frase ayuda, sean cuales sean los ajustes.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿Cómo vuelvo a los valores predeterminados?',
        answer: 'Pon la Sensibilidad de fin de intervención en 0,0, la Pausa máxima antes de finalizar en 2000 ms y el Nivel de reducción de latencia en 0 — Predeterminado.',
      },
      {
        question: '¿Hay un modo de pulsar para hablar?',
        answer: 'Sí. Bajo Modo de habla en la configuración, elige Pulsar para hablar: el audio se envía solo mientras mantienes pulsada la barra espaciadora o el botón del micrófono, y al soltar termina la frase. Pulsar para traducir es igual, salvo que, mientras no mantienes pulsado, tu propia voz va directamente al micrófono virtual. Los ajustes de esta página solo se aplican en el modo Auto.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content:
      '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Régler les coupures de phrase et la latence',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content:
      'KizunaAI traduit une phrase dès qu’il estime que vous l’avez terminée. Si les traductions arrivent trop tard, ou si les phrases sont coupées au milieu, <strong>Réglage de la détection de fin de parole</strong> modifie la façon dont cette décision est prise. Les valeurs par défaut conviennent à la plupart des conversations ; modifiez un réglage à la fois et essayez-le lors d’une courte session. Cette page concerne le moteur Soniox : avec le moteur Qwen, le serveur termine un tour de parole quand le locuteur change ou après une pause, et le seul réglage est <strong>Durée du silence</strong> sous Paramètres VAD, de 0,5 à 6 secondes (2,5 s par défaut), plus court pour des traductions plus rapides, plus long pour garder entières les phrases lentes.',
  },
  steps: [
    {
      title: 'Ouvrez les réglages',
      content:
        'Ouvrez les <strong>Paramètres</strong>, passez en <strong>Avancé</strong> avec le bouton en haut à droite du panneau, puis choisissez l’onglet <strong>Fournisseur</strong>. Faites défiler jusqu’à <strong>Réglage de la détection de fin de parole</strong>. Les modifications s’appliquent à partir de la session suivante.',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: 'Sensibilité de détection de fin de parole',
      content:
        'La facilité avec laquelle une pause est considérée comme la fin d’une phrase, de −1 à 1 (0 par défaut). Des valeurs plus élevées terminent les phrases plus tôt : les traductions arrivent plus vite, mais les phrases peuvent être coupées prématurément. Des valeurs plus basses attendent un signe plus net que vous avez fini.',
    },
    {
      title: 'Pause maximale avant finalisation',
      content:
        'Une limite stricte, de 500 à 3000 ms (2000 ms par défaut) : une fois que vous cessez de parler, la phrase est close au plus tard après ce délai. Augmentez-la si vous faites souvent des pauses en milieu de phrase et que vos phrases sont scindées ; diminuez-la pour des traductions plus rapides.',
    },
    {
      title: 'Niveau de réduction de la latence',
      content:
        'De <strong>0 — Par défaut</strong> à <strong>Le plus agressif</strong>, en passant par <strong>Latence réduite</strong> et <strong>Latence encore plus réduite</strong>. Chaque niveau clôt les phrases plus tôt pour réduire le délai.',
    },
  ],
  tips: {
    title: 'Conseils',
    items: [
      'Les traductions arrivent en retard : essayez d’augmenter légèrement la Sensibilité de détection de fin de parole, ou de choisir un Niveau de réduction de la latence plus élevé.',
      'Les phrases sont coupées au milieu : baissez la Sensibilité de détection de fin de parole, ou augmentez la Pause maximale avant finalisation.',
      'Marquer une courte pause à la fin de chaque phrase aide, quels que soient les réglages.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Comment revenir aux valeurs par défaut ?',
        answer: 'Réglez la Sensibilité de détection de fin de parole sur 0,0, la Pause maximale avant finalisation sur 2000 ms et le Niveau de réduction de la latence sur 0 — Par défaut.',
      },
      {
        question: 'Existe-t-il un mode push-to-talk ?',
        answer: 'Oui. Sous Mode de parole dans les Paramètres, choisissez Appuyer pour parler : l’audio n’est envoyé que pendant que vous maintenez Espace ou le bouton du microphone, et le relâcher termine la phrase. Appuyer pour traduire fonctionne de la même façon, sauf que, quand vous ne maintenez pas, votre propre voix va directement dans le microphone virtuel. Les réglages de cette page ne s’appliquent qu’en mode Auto.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content:
      'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'اضبط فواصل الجمل وزمن التأخير',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content:
      'يترجم KizunaAI الجملة بمجرد أن يقرر أنك أنهيتها. إذا وصلت الترجمات متأخرة جدًا، أو قُطعت الجمل في منتصفها، فإن <strong>ضبط اكتشاف نهاية الكلام</strong> يغيّر طريقة اتخاذ هذا القرار. تناسب القيم الافتراضية معظم المحادثات؛ غيّر إعدادًا واحدًا في كل مرة وجرّبه في جلسة قصيرة. تتناول هذه الصفحة محرك Soniox؛ أما مع محرك Qwen فينهي الخادم الدور عندما يتغير المتحدث أو بعد توقف قصير، والإعداد الوحيد هو <strong>مدة الصمت</strong> ضمن إعدادات VAD، من 0.5 إلى 6 ثوانٍ (2.5 ثانية افتراضيًا): قيمة أقصر لترجمات أسرع، وأطول للإبقاء على الجمل البطيئة متماسكة.',
  },
  steps: [
    {
      title: 'افتح إعدادات الضبط',
      content:
        'افتح <strong>الإعدادات</strong>، وانتقل إلى <strong>متقدم</strong> بالزر الموجود أعلى يمين اللوحة، ثم اختر علامة التبويب <strong>المزود</strong>. مرّر إلى <strong>ضبط اكتشاف نهاية الكلام</strong>. تُطبَّق التغييرات بدءًا من الجلسة التالية.',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: 'حساسية اكتشاف نهاية الكلام',
      content:
        'مدى سهولة اعتبار التوقف نهايةً للجملة، من −1 إلى 1 (الافتراضي 0). القيم الأعلى تُنهي الجمل أسرع، فتصل الترجمات أسرع، لكن قد تُقطع الجمل مبكرًا. أما القيم الأدنى فتنتظر دليلًا أقوى على أنك أنهيت كلامك.',
    },
    {
      title: 'أقصى مدة توقف قبل الإنهاء',
      content:
        'حدّ أقصى صارم، من 500 إلى 3000 ms (الافتراضي 2000 ms): بمجرد أن تتوقف عن الكلام، تُغلَق الجملة بعد هذه المدة على الأكثر. ارفعه إذا كنت تتوقف كثيرًا في منتصف الجملة فتنقسم جملك؛ واخفضه للحصول على ترجمات أسرع.',
    },
    {
      title: 'مستوى تقليل التأخير',
      content:
        'من <strong>0 — افتراضي</strong> مرورًا بـ <strong>تأخير أقل</strong> و<strong>تأخير أقل من ذلك</strong> وصولًا إلى <strong>الأكثر قوة</strong>. كل درجة تُغلق الجمل بسرعة أكبر لتقليل التأخير.',
    },
  ],
  tips: {
    title: 'نصائح',
    items: [
      'تصل الترجمات متأخرة: جرّب رفع حساسية اكتشاف نهاية الكلام قليلًا، أو اختيار مستوى أعلى لتقليل التأخير.',
      'تُقطع الجمل في منتصفها: اخفض حساسية اكتشاف نهاية الكلام، أو ارفع أقصى مدة توقف قبل الإنهاء.',
      'التوقف قليلًا في نهاية كل جملة يساعد أيًّا كانت الإعدادات.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'كيف أعود إلى القيم الافتراضية؟',
        answer: 'اضبط حساسية اكتشاف نهاية الكلام على 0.0، وأقصى مدة توقف قبل الإنهاء على 2000 ms، ومستوى تقليل التأخير على 0 — افتراضي.',
      },
      {
        question: 'هل يوجد وضع «اضغط للتحدث»؟',
        answer: 'نعم. تحت «وضع الكلام» في الإعدادات، اختر «اضغط للتحدث»: لا يُرسَل الصوت إلا ما دمت تضغط على مفتاح المسافة أو زر الميكروفون، وتركه يُنهي الجملة. أما «اضغط للترجمة» فمثله تمامًا، إلا أن صوتك الأصلي يذهب مباشرة إلى الميكروفون الافتراضي حين لا تضغط. ولا تُطبَّق الإعدادات في هذه الصفحة إلا في وضع «تلقائي».',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content:
      'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Ajuste as quebras de frase e a latência',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'A KizunaAI traduz uma frase assim que decide que você a terminou. Se as traduções chegam tarde demais, ou as frases são cortadas no meio, <strong>Ajuste de detecção de fim da fala</strong> muda a forma como essa decisão é tomada. Os padrões servem para a maioria das conversas; mude uma configuração por vez e teste em uma sessão curta. Esta página trata do mecanismo Soniox: com o mecanismo Qwen, o servidor encerra um turno quando a pessoa que fala muda ou depois de uma pausa, e a única configuração é <strong>Duração do silêncio</strong>, em Configurações VAD, de 0,5 a 6 segundos (2,5 s por padrão): menor para traduções mais rápidas, maior para manter inteiras as frases ditas devagar.',
  },
  steps: [
    {
      title: 'Abra as configurações de ajuste',
      content:
        'Abra <strong>Configurações</strong>, mude para <strong>Avançado</strong> com o botão no canto superior direito do painel e escolha a aba <strong>Provedor</strong>. Role até <strong>Ajuste de detecção de fim da fala</strong>. As mudanças valem a partir da próxima sessão.',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: 'Sensibilidade de fim da fala',
      content:
        'Com que facilidade uma pausa conta como o fim de uma frase, de −1 a 1 (padrão 0). Valores mais altos encerram as frases mais cedo, então as traduções chegam mais rápido, mas as frases podem ser cortadas antes da hora. Valores mais baixos esperam sinais mais claros de que você terminou.',
    },
    {
      title: 'Pausa máxima antes de finalizar',
      content:
        'Um limite rígido, de 500 a 3000 ms (padrão 2000 ms): depois que você para de falar, a frase é encerrada no máximo após esse tempo. Aumente-o se você costuma pausar no meio da frase e suas frases acabam divididas; diminua-o para ter traduções mais rápidas.',
    },
    {
      title: 'Nível de redução de latência',
      content:
        'De <strong>0 — Padrão</strong>, passando por <strong>Menor latência</strong> e <strong>Latência ainda menor</strong>, até <strong>Mais agressivo</strong>. Cada nível encerra as frases com mais pressa para reduzir o atraso.',
    },
  ],
  tips: {
    title: 'Dicas',
    items: [
      'As traduções chegam tarde: tente aumentar um pouco a Sensibilidade de fim da fala, ou usar um Nível de redução de latência mais alto.',
      'As frases são cortadas no meio: diminua a Sensibilidade de fim da fala, ou aumente a Pausa máxima antes de finalizar.',
      'Fazer uma breve pausa no fim de cada frase ajuda, sejam quais forem as configurações.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'Como volto aos padrões?',
        answer: 'Defina Sensibilidade de fim da fala como 0,0, Pausa máxima antes de finalizar como 2000 ms e Nível de redução de latência como 0 — Padrão.',
      },
      {
        question: 'Existe um modo de apertar para falar?',
        answer: 'Sim. Em Modo de fala, nas Configurações, escolha Pressionar para falar: o áudio é enviado só enquanto você mantém pressionada a tecla Espaço ou o botão do microfone, e soltar encerra a frase. Pressionar para traduzir é igual, exceto que, enquanto você não está pressionando, a sua própria voz vai direto para o microfone virtual. As configurações desta página só valem no modo Auto.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Настройте разбиение на предложения и задержку',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'KizunaAI переводит предложение, как только решает, что вы его закончили. Если перевод приходит слишком поздно или предложения обрываются на середине, раздел <strong>Настройка определения окончания реплики</strong> меняет то, как принимается это решение. Значения по умолчанию подходят для большинства разговоров; меняйте по одному параметру и проверяйте его в коротком сеансе. Эта страница о движке Soniox: у движка Qwen сервер завершает реплику, когда меняется говорящий или после паузы, а единственный параметр — <strong>Длительность тишины</strong> в разделе «Настройки VAD», от 0.5 до 6 секунд (по умолчанию 2.5 с): чем меньше значение, тем быстрее перевод, чем больше — тем реже медленные предложения разрываются на части.',
  },
  steps: [
    {
      title: 'Откройте параметры настройки',
      content:
        'Откройте <strong>Настройки</strong>, переключитесь в режим <strong>Расширенный</strong> кнопкой в правом верхнем углу панели и выберите вкладку <strong>Поставщик</strong>. Прокрутите до раздела <strong>Настройка определения окончания реплики</strong>. Изменения действуют со следующего сеанса.',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: 'Чувствительность определения окончания реплики',
      content:
        'Насколько легко пауза считается концом предложения, от −1 до 1 (по умолчанию 0). Более высокие значения завершают предложения раньше, поэтому перевод приходит быстрее, но предложения могут обрываться преждевременно. Более низкие значения ждут более явных признаков того, что вы закончили.',
    },
    {
      title: 'Максимальная пауза перед завершением',
      content:
        'Жёсткий предел от 500 до 3000 мс (по умолчанию 2000 мс): после того как вы замолчали, предложение закрывается не позже чем через это время. Увеличьте его, если вы часто делаете паузы посреди предложения и ваши предложения разбиваются; уменьшите — чтобы перевод приходил быстрее.',
    },
    {
      title: 'Уровень снижения задержки',
      content:
        'От <strong>0 — По умолчанию</strong> через <strong>Меньшая задержка</strong> и <strong>Ещё меньшая задержка</strong> до <strong>Максимально агрессивный</strong>. Каждый шаг закрывает предложения охотнее, чтобы сократить задержку.',
    },
  ],
  tips: {
    title: 'Советы',
    items: [
      'Перевод приходит поздно: попробуйте немного повысить «Чувствительность определения окончания реплики» или выбрать более высокое значение «Уровень снижения задержки».',
      'Предложения обрываются на середине: понизьте «Чувствительность определения окончания реплики» или увеличьте «Максимальная пауза перед завершением».',
      'Короткая пауза в конце каждого предложения помогает при любых настройках.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Как вернуть значения по умолчанию?',
        answer: 'Задайте значения: «Чувствительность определения окончания реплики» — 0.0, «Максимальная пауза перед завершением» — 2000 мс, «Уровень снижения задержки» — «0 — По умолчанию».',
      },
      {
        question: 'Есть ли режим «нажми и говори»?',
        answer: 'Да. В настройках, в разделе «Режим речи», выберите «Нажми и говори»: звук отправляется только пока вы удерживаете пробел или кнопку микрофона, а отпускание завершает предложение. «Нажми и переведи» работает так же, только пока вы не удерживаете, ваш собственный голос идёт напрямую на виртуальный микрофон. Параметры на этой странице действуют только в режиме «Авто».',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'Satzenden und Latenz feinabstimmen',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'KizunaAI übersetzt einen Satz, sobald es entscheidet, dass Sie ihn beendet haben. Kommen Übersetzungen zu spät oder werden Sätze mittendrin abgeschnitten, ändert <strong>Feinabstimmung der Sprachende-Erkennung</strong>, wie diese Entscheidung getroffen wird. Die Standardwerte passen für die meisten Gespräche; ändern Sie jeweils nur eine Einstellung und testen Sie sie in einer kurzen Sitzung. Diese Seite behandelt die Soniox-Engine: Bei der Qwen-Engine beendet der Server einen Redebeitrag, wenn der Sprecher wechselt oder nach einer Pause, und die einzige Einstellung ist <strong>Stille-Dauer</strong> unter VAD-Einstellungen, von 0.5 bis 6 Sekunden (standardmäßig 2.5 s), kürzer für schnellere Übersetzungen, länger, damit langsam gesprochene Sätze zusammenbleiben.',
  },
  steps: [
    {
      title: 'Feinabstimmung öffnen',
      content:
        'Öffnen Sie die <strong>Einstellungen</strong>, wechseln Sie mit der Schaltfläche oben rechts im Bereich zu <strong>Erweitert</strong> und wählen Sie den Tab <strong>Anbieter</strong>. Scrollen Sie zu <strong>Feinabstimmung der Sprachende-Erkennung</strong>. Änderungen gelten ab der nächsten Sitzung.',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: 'Empfindlichkeit der Sprachende-Erkennung',
      content:
        'Wie schnell eine Pause als Satzende gilt, von −1 bis 1 (Standard 0). Höhere Werte beenden Sätze früher, sodass Übersetzungen schneller kommen, Sätze aber zu früh abgeschnitten werden können. Niedrigere Werte warten auf deutlichere Anzeichen, dass Sie fertig sind.',
    },
    {
      title: 'Maximale Pause vor dem Abschluss',
      content:
        'Eine feste Obergrenze von 500 bis 3000 ms (Standard 2000 ms): Sobald Sie aufhören zu sprechen, wird der Satz spätestens nach dieser Zeit abgeschlossen. Erhöhen Sie den Wert, wenn Sie oft mitten im Satz innehalten und Ihre Sätze geteilt werden; senken Sie ihn für schnellere Übersetzungen.',
    },
    {
      title: 'Stufe der Latenzreduzierung',
      content:
        'Von <strong>0 — Standard</strong> über <strong>Geringere Latenz</strong> und <strong>Noch geringere Latenz</strong> bis <strong>Am aggressivsten</strong>. Jede Stufe schließt Sätze bereitwilliger ab, um die Verzögerung zu verringern.',
    },
  ],
  tips: {
    title: 'Tipps',
    items: [
      'Übersetzungen kommen spät: Erhöhen Sie „Empfindlichkeit der Sprachende-Erkennung“ ein wenig oder wählen Sie eine höhere „Stufe der Latenzreduzierung“.',
      'Sätze werden mittendrin abgeschnitten: Senken Sie „Empfindlichkeit der Sprachende-Erkennung“ oder erhöhen Sie „Maximale Pause vor dem Abschluss“.',
      'Eine kurze Pause am Ende jedes Satzes hilft, unabhängig von den Einstellungen.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Wie stelle ich die Standardwerte wieder her?',
        answer: 'Setzen Sie „Empfindlichkeit der Sprachende-Erkennung“ auf 0.0, „Maximale Pause vor dem Abschluss“ auf 2000 ms und „Stufe der Latenzreduzierung“ auf „0 — Standard“.',
      },
      {
        question: 'Gibt es einen Push-to-Talk-Modus?',
        answer: 'Ja. Wählen Sie in den Einstellungen unter „Sprachmodus“ „Push-to-Talk“: Audio wird nur gesendet, solange Sie die Leertaste oder die Mikrofon-Schaltfläche gedrückt halten; Loslassen beendet den Satz. „Push-to-Translate“ ist dasselbe, nur dass Ihre eigene Stimme, solange Sie nicht drücken, direkt an das virtuelle Mikrofon geht. Die Einstellungen auf dieser Seite gelten nur im Modus „Auto“.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Налаштуйте розбиття на речення й затримку',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'KizunaAI перекладає речення, щойно вирішує, що ви його закінчили. Якщо переклад надходить надто пізно або речення обриваються посередині, розділ <strong>Налаштування визначення кінця репліки</strong> змінює те, як ухвалюється це рішення. Значення за замовчуванням підходять для більшості розмов; змінюйте по одному параметру й перевіряйте його в короткому сеансі. Ця сторінка стосується рушія Soniox: у рушія Qwen сервер завершує репліку, коли змінюється мовець або після паузи, а єдине налаштування — <strong>Тривалість тиші</strong> в розділі «Налаштування VAD», від 0.5 до 6 секунд (за замовчуванням 2.5 с): коротша — для швидшого перекладу, довша — щоб повільні речення лишалися цілими.',
  },
  steps: [
    {
      title: 'Відкрийте параметри налаштування',
      content:
        'Відкрийте <strong>Налаштування</strong>, перейдіть у режим <strong>Розширений</strong> кнопкою в правому верхньому куті панелі та виберіть вкладку <strong>Постачальник</strong>. Прокрутіть до розділу <strong>Налаштування визначення кінця репліки</strong>. Зміни діють з наступного сеансу.',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: 'Чутливість визначення кінця репліки',
      content:
        'Наскільки легко пауза вважається кінцем речення, від −1 до 1 (за замовчуванням 0). Вищі значення завершують речення раніше, тож переклад надходить швидше, але речення можуть обриватися передчасно. Нижчі значення чекають чіткіших ознак того, що ви закінчили.',
    },
    {
      title: 'Максимальна пауза перед завершенням',
      content:
        'Жорстка межа від 500 до 3000 мс (за замовчуванням 2000 мс): після того як ви замовкли, речення закривається не пізніше ніж через цей час. Збільште її, якщо ви часто робите паузи посеред речення і ваші речення розбиваються; зменште — щоб переклад надходив швидше.',
    },
    {
      title: 'Рівень зменшення затримки',
      content:
        'Від <strong>0 — За замовчуванням</strong> через <strong>Нижча затримка</strong> і <strong>Ще нижча затримка</strong> до <strong>Найагресивніший</strong>. Кожен крок закриває речення охочіше, щоб скоротити затримку.',
    },
  ],
  tips: {
    title: 'Поради',
    items: [
      'Переклад надходить пізно: спробуйте трохи підвищити «Чутливість визначення кінця репліки» або вибрати вище значення «Рівень зменшення затримки».',
      'Речення обриваються посередині: знизьте «Чутливість визначення кінця репліки» або збільште «Максимальна пауза перед завершенням».',
      'Коротка пауза в кінці кожного речення допомагає за будь-яких налаштувань.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Як повернути значення за замовчуванням?',
        answer: 'Задайте значення: «Чутливість визначення кінця репліки» — 0.0, «Максимальна пауза перед завершенням» — 2000 мс, «Рівень зменшення затримки» — «0 — За замовчуванням».',
      },
      {
        question: 'Чи є режим «натисни й говори»?',
        answer: 'Так. У налаштуваннях, у розділі «Режим мовлення», виберіть «Натисни і говори»: аудіо надсилається лише поки ви тримаєте пробіл або кнопку мікрофона, а відпускання завершує речення. «Натисни і переклади» працює так само, тільки поки ви не тримаєте, ваш власний голос іде прямо на віртуальний мікрофон. Налаштування на цій сторінці діють лише в режимі «Авто».',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Regola le pause tra le frasi e la latenza',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'KizunaAI traduce una frase non appena decide che l’hai terminata. Se le traduzioni arrivano troppo tardi, o le frasi vengono tagliate a metà, <strong>Regolazione del rilevamento di fine frase</strong> cambia il modo in cui viene presa questa decisione. I valori predefiniti vanno bene per la maggior parte delle conversazioni; cambia un’impostazione alla volta e provala in una sessione breve. Questa pagina riguarda il motore Soniox: con il motore Qwen, il server chiude un turno quando cambia chi parla o dopo una pausa, e l’unica impostazione è <strong>Durata del silenzio</strong> in Impostazioni VAD, da 0,5 a 6 secondi (2,5 s per impostazione predefinita), più breve per traduzioni più rapide, più lunga per mantenere intere le frasi lente.',
  },
  steps: [
    {
      title: 'Apri le impostazioni di regolazione',
      content:
        'Apri <strong>Impostazioni</strong>, passa ad <strong>Avanzato</strong> con il pulsante in alto a destra del pannello e scegli la scheda <strong>Fornitore</strong>. Scorri fino a <strong>Regolazione del rilevamento di fine frase</strong>. Le modifiche valgono dalla sessione successiva.',
      screenshot: '01-endpoint-tuning.png',
    },
    {
      title: 'Sensibilità di fine frase',
      content:
        'Quanto facilmente una pausa conta come fine di una frase, da −1 a 1 (predefinito 0). Valori più alti chiudono le frasi prima, quindi le traduzioni arrivano più in fretta, ma le frasi possono essere tagliate troppo presto. Valori più bassi aspettano segnali più chiari che hai finito.',
    },
    {
      title: 'Pausa massima prima della finalizzazione',
      content:
        'Un limite fisso, da 500 a 3000 ms (predefinito 2000 ms): quando smetti di parlare, la frase viene chiusa al massimo dopo questo tempo. Aumentalo se fai spesso pause a metà frase e le tue frasi vengono spezzate; riducilo per avere traduzioni più rapide.',
    },
    {
      title: 'Livello di riduzione della latenza',
      content:
        'Da <strong>0 — Predefinito</strong>, passando per <strong>Latenza inferiore</strong> e <strong>Latenza ancora più bassa</strong>, fino a <strong>Più aggressivo</strong>. Ogni livello chiude le frasi con più prontezza per ridurre il ritardo.',
    },
  ],
  tips: {
    title: 'Consigli',
    items: [
      'Le traduzioni arrivano in ritardo: prova ad alzare un po’ Sensibilità di fine frase, oppure a scegliere un Livello di riduzione della latenza più alto.',
      'Le frasi vengono tagliate a metà: abbassa Sensibilità di fine frase, oppure aumenta Pausa massima prima della finalizzazione.',
      'Fare una breve pausa alla fine di ogni frase aiuta, qualunque siano le impostazioni.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'Come torno ai valori predefiniti?',
        answer: 'Imposta Sensibilità di fine frase su 0,0, Pausa massima prima della finalizzazione su 2000 ms e Livello di riduzione della latenza su 0 — Predefinito.',
      },
      {
        question: 'Esiste una modalità push-to-talk?',
        answer: 'Sì. In Impostazioni, sotto Modalità vocale, scegli Premi per parlare: l’audio viene inviato solo mentre tieni premuto Spazio o il pulsante del microfono, e rilasciandolo la frase termina. Premi per tradurre è uguale, salvo che mentre non tieni premuto la tua voce va direttamente nel microfono virtuale. Le impostazioni di questa pagina si applicano solo in modalità Auto.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function KizunaLatencyGuide() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/kizuna-ai-latency" />;
}
